import { BusinessPartnerBody } from '../../api/generated/pm';

export interface OrganizationNode extends BusinessPartnerBody {
  children: OrganizationNode[];
  level: number;
  isLastChild: boolean;
}

export const getOrganizationAuthURL = (organizationId?: string) => {
  if (!organizationId) return null;
  return `/api/auth?bind_type=topcon_support&tcbp=${encodeURIComponent(organizationId)}`;
};

/**
 * Builds a hierarchical tree from a flat list of organizations
 */
export const buildOrganizationHierarchy = (
  organizations: BusinessPartnerBody[]
): OrganizationNode[] => {
  const nodeMap = new Map<string, OrganizationNode>();
  const rootNodes: OrganizationNode[] = [];

  // First pass: create all nodes
  organizations.forEach((org) => {
    nodeMap.set(org.id, {
      ...org,
      children: [],
      level: 0,
      isLastChild: false,
    });
  });

  // Second pass: build parent-child relationships
  organizations.forEach((org) => {
    const node = nodeMap.get(org.id)!;
    if (org.parentId && nodeMap.has(org.parentId)) {
      const parent = nodeMap.get(org.parentId)!;
      parent.children.push(node);
      node.level = parent.level + 1;
    } else {
      // No parent or parent not found - treat as root
      rootNodes.push(node);
    }
  });

  // Third pass: mark last children
  const markLastChildren = (nodes: OrganizationNode[]) => {
    nodes.forEach((node, index) => {
      node.isLastChild = index === nodes.length - 1;
      if (node.children.length > 0) {
        markLastChildren(node.children);
      }
    });
  };
  markLastChildren(rootNodes);

  return rootNodes;
};

/**
 * Flattens a hierarchical tree back into a list while preserving order and level
 */
export const flattenOrganizationHierarchy = (nodes: OrganizationNode[]): OrganizationNode[] => {
  const result: OrganizationNode[] = [];

  const traverse = (node: OrganizationNode) => {
    result.push(node);
    node.children.forEach(traverse);
  };

  nodes.forEach(traverse);
  return result;
};
